import { useEffect } from "react";

interface FaqItem {
  q: string;
  a: string;
}

interface SeoHeadProps {
  title: string;
  description: string;
  /** Absolute or root-relative URL to the social preview image for this page. */
  image?: string;
  /** Explicit path override; defaults to window.location.pathname. */
  path?: string;
  faqs?: FaqItem[];
}

const SITE_ORIGIN = "https://wellnessacademyusa.com";

const setMeta = (attr: "name" | "property", key: string, content: string) => {
  let el = document.head.querySelector<HTMLMetaElement>(`meta[${attr}="${key}"]`);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(attr, key);
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
};

const setCanonical = (href: string) => {
  let el = document.head.querySelector<HTMLLinkElement>('link[rel="canonical"]');
  if (!el) {
    el = document.createElement("link");
    el.setAttribute("rel", "canonical");
    document.head.appendChild(el);
  }
  el.setAttribute("href", href);
};

const toAbsolute = (url: string) => {
  try {
    return new URL(url, SITE_ORIGIN).href;
  } catch {
    return url;
  }
};

const SeoHead = ({ title, description, image, path, faqs }: SeoHeadProps) => {
  useEffect(() => {
    const pagePath = path ?? (typeof window !== "undefined" ? window.location.pathname : "/");
    const pageUrl = toAbsolute(pagePath);

    document.title = title;
    setMeta("name", "description", description);

    // Open Graph
    setMeta("property", "og:title", title);
    setMeta("property", "og:description", description);
    setMeta("property", "og:url", pageUrl);
    setMeta("property", "og:type", pagePath.startsWith("/blog/") ? "article" : "website");

    // Twitter
    setMeta("name", "twitter:card", "summary_large_image");
    setMeta("name", "twitter:title", title);
    setMeta("name", "twitter:description", description);

    if (image) {
      const abs = toAbsolute(image);
      setMeta("property", "og:image", abs);
      setMeta("name", "twitter:image", abs);
    }

    setCanonical(pageUrl);

    // FAQ schema
    const existingScript = document.querySelector('script[data-faq-schema]');
    if (existingScript) existingScript.remove();

    if (faqs && faqs.length > 0) {
      const schema = {
        "@context": "https://schema.org",
        "@type": "FAQPage",
        mainEntity: faqs.map((faq) => ({
          "@type": "Question",
          name: faq.q,
          acceptedAnswer: {
            "@type": "Answer",
            text: faq.a,
          },
        })),
      };
      const script = document.createElement("script");
      script.type = "application/ld+json";
      script.setAttribute("data-faq-schema", "true");
      script.textContent = JSON.stringify(schema);
      document.head.appendChild(script);
    }

    return () => {
      const s = document.querySelector('script[data-faq-schema]');
      if (s) s.remove();
    };
  }, [title, description, image, path, faqs]);

  return null;
};

export default SeoHead;
